import * as Sentry from "@sentry/react";
import type { ErrorEvent, EventHint } from "@sentry/react";
import { logPdfGenerationBuild } from "./pdfGenerationBuild";

/**
 * Returns true when a message is from React Router's Vite HMR runtime.
 *
 * These throws are development-only (they originate from `virtual:react-router/hmr-runtime`).
 * They are benign (a full reload recovers) but were being reported to Sentry whenever
 * `VITE_SENTRY_DSN` is set during `npm run dev`.
 *
 * @param message - Error or log message text
 * @returns Whether this message should be dropped before sending to Sentry
 */
export function isReactRouterHmrSentryNoise(
  message: string | undefined | null
): boolean {
  if (message == null || message === "") return false;
  return message.includes("[react-router:hmr]");
}

/**
 * Collects human-readable messages from a Sentry error event for filtering.
 *
 * @param event - Sentry error event payload
 * @param hint - Original exception and other SDK metadata
 * @returns Candidate message strings
 */
function collectSentryErrorMessages(
  event: ErrorEvent,
  hint: EventHint
): string[] {
  const out: string[] = [];
  if (event.message) out.push(event.message);
  for (const v of event.exception?.values ?? []) {
    if (v.value) out.push(v.value);
  }
  const orig = hint.originalException;
  if (orig instanceof Error && orig.message) out.push(orig.message);
  else if (typeof orig === "string" && orig) out.push(orig);
  return out;
}

/**
 * Initialize Sentry for error tracking, performance monitoring, and structured logging.
 *
 * This should be called as early as possible in the application lifecycle,
 * ideally before React hydration in entry.client.tsx.
 */
/** True when the current page is the headless PDF renderer (Puppeteer target). */
export function isPdfGenerationPage(): boolean {
  if (typeof window === "undefined") return false;
  return window.location.pathname === "/pdf";
}

export function initSentry(): void {
  // Only initialize in production builds. Local `npm run dev` must not report to
  // the production Sentry project when VITE_SENTRY_DSN is set in .env.
  if (!import.meta.env.PROD) return;

  // Sentry envelope POSTs (/api/{project_id}/envelope/) can hang on third-party
  // ingest and must not run during PDF capture — they are unrelated to report data.
  if (isPdfGenerationPage()) {
    logPdfGenerationBuild("sentry-skipped-on-pdf-route");
    return;
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN;

  // Only initialize if DSN is configured
  if (!dsn) {
    if (import.meta.env.DEV) {
      console.warn(
        "[Sentry] VITE_SENTRY_DSN is not set. Sentry will not be initialized."
      );
    }
    return;
  }

  // Enable Sentry by default when DSN is set, unless explicitly disabled
  const isEnabled = import.meta.env.VITE_SENTRY_ENABLED !== "false";

  Sentry.init({
    dsn,

    // Send default PII data (e.g., IP address) to Sentry
    sendDefaultPii: true,

    // Performance Monitoring - lower sample rates to avoid rate limiting
    // 10% in dev, 5% in production
    tracesSampleRate: import.meta.env.DEV ? 0.1 : 0.05,

    // Enable structured logging
    _experiments: {
      enableLogs: true,
    },

    // Set environment based on Vite mode
    environment: import.meta.env.MODE,

    // Enable/disable based on config
    enabled: isEnabled,

    // Integrations with conservative settings
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Session replay sample rates - conservative to avoid rate limiting
    replaysSessionSampleRate: 0.01, // 1% of sessions
    replaysOnErrorSampleRate: 0.5, // 50% of sessions with errors

    // Debug mode only in development when explicitly enabled
    debug: import.meta.env.VITE_SENTRY_DEBUG === "true",

    // Drop React Router Vite HMR noise (dev-only; not actionable product bugs)
    beforeSend(event, hint) {
      for (const msg of collectSentryErrorMessages(event, hint)) {
        if (isReactRouterHmrSentryNoise(msg)) return null;
      }
      return event;
    },
  });
}

// Re-export Sentry for convenience
export { Sentry };

// Export logger for structured logging
export const { logger } = Sentry;
